/**
 * Generate a branded 1080x1080 PNG card celebrating a tonnage gain and
 * share it via the Web Share API (with a download fallback on desktop).
 *
 * Kept dependency-free: pure Canvas2D, system-bold sans-serif so it works
 * on iOS Safari, Android Chrome and desktop without font preload.
 */

function drawCard({ gainKg, gainPct, dayName }) {
    const size = 1080;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");

    // Background — deep obsidian with a soft red radial in the top-right.
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, size, size);
    const radial = ctx.createRadialGradient(size * 0.85, size * 0.15, 60, size * 0.85, size * 0.15, size * 0.9);
    radial.addColorStop(0, "rgba(255,59,48,0.32)");
    radial.addColorStop(1, "rgba(255,59,48,0)");
    ctx.fillStyle = radial;
    ctx.fillRect(0, 0, size, size);

    // Tactical corner brackets (top-left + bottom-right) for the Hypergains look.
    ctx.strokeStyle = "#FF3B30";
    ctx.lineWidth = 6;
    const pad = 60;
    const brk = 80;
    ctx.beginPath();
    ctx.moveTo(pad, pad + brk); ctx.lineTo(pad, pad); ctx.lineTo(pad + brk, pad);
    ctx.moveTo(size - pad - brk, size - pad); ctx.lineTo(size - pad, size - pad); ctx.lineTo(size - pad, size - pad - brk);
    ctx.stroke();

    // Eyebrow.
    ctx.fillStyle = "#FF3B30";
    ctx.font = '700 28px "Helvetica Neue", Arial, sans-serif';
    ctx.textAlign = "left";
    ctx.fillText("THE BAR IS MOVING", pad + 30, pad + 160);

    // Hero number — gain in kg, massive.
    ctx.fillStyle = "#ffffff";
    ctx.font = '900 240px "Helvetica Neue", Arial Black, sans-serif';
    const heroLabel = `+${gainKg} KG`;
    ctx.fillText(heroLabel, pad + 28, pad + 380);

    // Sub-hero — day name.
    ctx.fillStyle = "#ffffff";
    ctx.font = '900 80px "Helvetica Neue", Arial Black, sans-serif';
    const dayLine = (dayName || "Session").toUpperCase();
    ctx.fillText(`ON ${dayLine}`, pad + 30, pad + 480);

    // Percent pill.
    const pctText = `+${gainPct}% VS LAST SESSION`;
    ctx.font = '800 36px "Helvetica Neue", Arial, sans-serif';
    const pctW = ctx.measureText(pctText).width;
    const pillX = pad + 30;
    const pillY = pad + 520;
    const pillW = pctW + 56;
    const pillH = 72;
    ctx.fillStyle = "rgba(255,59,48,0.18)";
    ctx.strokeStyle = "#FF3B30";
    ctx.lineWidth = 3;
    ctx.fillRect(pillX, pillY, pillW, pillH);
    ctx.strokeRect(pillX, pillY, pillW, pillH);
    ctx.fillStyle = "#FF3B30";
    ctx.fillText(pctText, pillX + 28, pillY + 50);

    // Body copy block.
    ctx.fillStyle = "rgba(255,255,255,0.78)";
    ctx.font = '500 32px "Helvetica Neue", Arial, sans-serif';
    const copyLines = [
        "Mechanical tension, four-to-eight reps,",
        "RIR 1–2. Progressive overload doing its job.",
    ];
    copyLines.forEach((line, i) => {
        ctx.fillText(line, pad + 30, size - pad - 200 + i * 44);
    });

    // Brand line at the very bottom.
    ctx.fillStyle = "#ffffff";
    ctx.font = '900 56px "Helvetica Neue", Arial Black, sans-serif';
    ctx.textAlign = "left";
    ctx.fillText("HYPERGAINS", pad + 30, size - pad - 60);

    ctx.fillStyle = "#FF3B30";
    ctx.font = '700 28px "Helvetica Neue", Arial, sans-serif';
    ctx.textAlign = "right";
    ctx.fillText("hypergains.fit", size - pad - 30, size - pad - 60);

    return canvas;
}

export async function shareProgressCard({ gainKg, gainPct, dayName }) {
    const canvas = drawCard({ gainKg, gainPct, dayName });
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
    if (!blob) throw new Error("Card render failed");

    const filename = `hypergains-gain-${Date.now()}.png`;
    const file = new File([blob], filename, { type: "image/png" });

    // Prefer Web Share API w/ files (mobile, iOS 15+, Android Chrome).
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
            await navigator.share({
                files: [file],
                title: "Hypergains gain",
                text: `+${gainKg}kg on ${dayName || "my last session"} (+${gainPct}%). Hypergains.`,
            });
            return { shared: true };
        } catch (e) {
            // User cancelled — silently no-op.
            if (e?.name === "AbortError") return { shared: false, cancelled: true };
            // Fall through to download.
        }
    }

    // Fallback: trigger download.
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    return { shared: false, downloaded: true };
}
